import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { CLOUD_API_URL } from '../config/serverConfig';
import { getHeaders } from '../lib/useQobuz';
import { normalizeAlbum } from '../utils/albumUtils';
import { HiResAudioBadgeForAlbum } from './HiResAudioBadge';

const GOLD = '#C9A84C';

export default function GenreBrowseSlideout({ genreId, genreName, source, isOpen, onClose, onOpenAlbum }) {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [closing, setClosing] = useState(false);
  const panelRef = useRef(null);

  const dismiss = useCallback(() => {
    setClosing(true);
    setTimeout(() => onClose?.(), 300);
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      setClosing(false);
      return;
    }

    const handleKey = (e) => { if (e.key === 'Escape') dismiss(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, dismiss]);

  useEffect(() => {
    if (!isOpen || (!genreId && !genreName)) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAlbums([]);

    const getCover = (a) =>
      a?.image?.large || a?.image?.small || a?.image?.thumbnail ||
      (typeof a?.image === 'string' ? a.image : null) ||
      a?.cover || a?.coverUrl || a?.cover_url || null;

    const getArtist = (a) =>
      a?.artist?.name || a?.artistName || a?.artist_name ||
      (typeof a?.artist === 'string' ? a.artist : '') || '';

    const mapItems = (items) =>
      items.map((a) => normalizeAlbum({
        ...a,
        id: a.id,
        serviceAlbumId: String(a.id),
        title: a.title || '',
        artist: getArtist(a),
        artistId: a.artist?.id ? String(a.artist.id) : (a.artistId || null),
        coverUrl: getCover(a),
        maxBitDepth: a.maximum_bit_depth || a.maxBitDepth || null,
        maxSampleRate: a.maximum_sampling_rate || a.maxSampleRate || null,
        releaseDate: a.released_at || a.release_date_original || a.releaseDate || null,
        genre: a.genre,
        genres: a.genres,
        source: 'qobuz',
      }, 'qobuz')).filter(Boolean);

    (async () => {
      try {
        const hdrs = getHeaders();
        let items = [];

        if (genreId) {
          const params = new URLSearchParams({ genre_id: String(genreId), limit: '24' });
          if (genreName) params.set('genre_name', genreName);
          const url = `${CLOUD_API_URL}/api/streaming/qobuz/genre-albums?${params}`;
          try {
            const resp = await fetch(url, { headers: hdrs });
            if (resp.ok) {
              const data = await resp.json();
              if (cancelled) return;
              items = data?.albums?.items || data?.albums || data?.items || [];
            }
          } catch { /* fallback below */ }
        }

        if (items.length === 0 && genreName) {
          try {
            const url = `${CLOUD_API_URL}/api/streaming/qobuz/genre-albums?genre_name=${encodeURIComponent(genreName)}&limit=24`;
            const resp = await fetch(url, { headers: hdrs });
            if (resp.ok) {
              const data = await resp.json();
              if (cancelled) return;
              items = data?.albums?.items || data?.albums || data?.items || [];
            }
          } catch { /* final fallback failed */ }
        }

        setAlbums(mapItems(items));
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, genreId, genreName]);

  if (!isOpen) return null;

  const handleAlbumClick = (album) => {
    dismiss();
    setTimeout(() => onOpenAlbum?.(album), 320);
  };

  const content = (
    <>
      {/* Backdrop */}
      <div
        onClick={dismiss}
        style={{
          position: 'fixed', inset: 0, zIndex: 9990,
          background: 'rgba(0,0,0,0.4)',
          animation: closing ? 'fadeOut 0.3s ease forwards' : 'fadeIn 0.2s ease',
        }}
      />

      {/* Slideout panel */}
      <div
        ref={panelRef}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 9991,
          width: 'min(480px, 100vw)',
          background: 'var(--bg-primary)',
          borderLeft: '1px solid var(--border)',
          boxShadow: '-8px 0 40px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column',
          animation: closing ? 'slideOutRight 0.3s ease forwards' : 'slideInRight 0.3s cubic-bezier(0.32,0.72,0,1)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '16px 20px',
          borderBottom: '1px solid var(--border)',
          paddingTop: 'max(16px, env(safe-area-inset-top, 16px))',
          background: 'var(--bg-secondary)',
          flexShrink: 0,
        }}>
          <button
            onClick={dismiss}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-secondary)', marginRight: 12 }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <h2 style={{ flex: 1, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {genreName || 'Genre'}
          </h2>
          <button
            onClick={dismiss}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-secondary)' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }} className="no-scrollbar">
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 60 }}>
              <div style={{ width: 28, height: 28, border: '3px solid var(--border)', borderTopColor: GOLD, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            </div>
          )}

          {error && !loading && (
            <div style={{ textAlign: 'center', paddingTop: 60, color: 'var(--text-secondary)' }}>
              <p style={{ fontSize: 14 }}>Failed to load genre albums</p>
              <p style={{ fontSize: 12, marginTop: 4, opacity: 0.6 }}>{error}</p>
            </div>
          )}

          {!loading && !error && albums.length === 0 && (
            <div style={{ textAlign: 'center', paddingTop: 60, color: 'var(--text-secondary)', fontSize: 14 }}>
              No albums found for this genre
            </div>
          )}

          {!loading && albums.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 16 }}>
              {albums.map((album) => (
                <button
                  key={album.id}
                  type="button"
                  onClick={() => handleAlbumClick(album)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}
                >
                  <div style={{ position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', background: 'var(--bg-secondary)' }}>
                    {album.coverUrl ? (
                      <img src={album.coverUrl} alt={album.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', fontSize: 24 }}>
                        ♪
                      </div>
                    )}
                    <HiResAudioBadgeForAlbum album={album} height={16} className="absolute top-1.5 right-1.5" />
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {album.title}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                      {album.artist}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(content, document.body);
}
